import React from 'react';
import { Box, Inscription, Preloader } from '@common-fe/common-fe';

import AppButton from '@/components/controls/AppButton';
import { WarnModal } from '@/components/elements';

import { TerminationSteps } from '../store/useEmployerTerminationModal.store';

import { useTerminateEmployerModal } from './hooks/useTerminateEmployerModal';
import { EmployerTerminationDateForm } from './EmployerTerminationDateForm';
import { EmployerTerminationHsaTransferForm } from './EmployerTerminationHsaTransferForm';
import { EmployerTerminationInfoBanner } from './EmployerTerminationInfoBanner';
import { EmployerTerminationPlanList } from './EmployerTerminationPlanList';
import { EmployerTerminationPlansCoverageDatesList } from './EmployerTerminationPlansCoverageDatesList';

const INVALID_DATE_RANGE_TEXT = 'Termination date must be within the current plan dates and no more than 6 months in advance.';

interface Props {
  onCancel?: (value: boolean) => void;
}

const EmployerTerminationModalForm: React.FC<Props> = ({ onCancel }) => {
  const {
    isThereTerminationDate,
    modalDescription,
    terminationDataLoading,
    initialLoading,
    step,
    changeValues,
    isSubmit,
    setDateIsFilled,
    terminationDate,
    terminationData,
    permanentLastDate,
    planListLoading,
    plansData,
    coverageData,
    isHsaAccountExist,
    setHsaIsFilled,
    isTerminationDateError,
    editMode,
    handleCancelTermination,
    handleCancel,
    terminationLoading,
    handleSubmit,
    currentButtonTitle,
    isServerError,
    setIsServerError,
    planCoveragesErrors,
    formValues,
    hsaTransferOfAssets,
  } = useTerminateEmployerModal({ onCancel });

  return (
    <>
      {isThereTerminationDate ? (
        <Box margin={{ bottom: 'spacing24' }}>
          <Inscription textAlign="center" size="14px">
            {modalDescription}
          </Inscription>
        </Box>
      ) : null}

      <Box data-testid="EmployerTerminationModalForm_container">
        <Box direction="column">
          <Box round="container1Round" pad="spacing24" background="module">
            {terminationDataLoading || initialLoading ? (
              <Preloader />
            ) : (
              <Box gap="spacing24">
                {step === TerminationSteps.SET_TERMINATION_DATE ? (
                  <EmployerTerminationDateForm
                    handleChangeValues={changeValues}
                    isSubmit={isSubmit}
                    setSetup={setDateIsFilled}
                    terminationDate={terminationDate || terminationData?.terminationDate}
                    minDate={permanentLastDate}
                    isExternalError={isTerminationDateError}
                  />
                ) : null}

                {/* {planListLoading && editMode ? (
                  <Box width="100%">
                    <ShimmerBox
                      width="100%"
                      height="200px"
                    />
                  </Box>
                ) : null} */}

                {isThereTerminationDate &&
                !planListLoading &&
                plansData &&
                plansData.length === 0 &&
                step === TerminationSteps.SET_TERMINATION_DATE ? (
                  <EmployerTerminationPlanList planList={plansData} />
                ) : null}

                {step === TerminationSteps.SET_TERMINATION_PARAMS ? (
                  <EmployerTerminationPlansCoverageDatesList
                    planList={coverageData}
                    withEdit
                    planCoveragesErrors={planCoveragesErrors}
                  />
                ) : null}

                {step === TerminationSteps.SET_TERMINATION_PARAMS && isHsaAccountExist ? (
                  <EmployerTerminationHsaTransferForm
                    handleChangeValues={changeValues}
                    isSubmit={isSubmit}
                    setSetup={setHsaIsFilled}
                    defaultHsaTransferOfAssets={formValues.hsaTransferOfAssets || hsaTransferOfAssets}
                  />
                ) : null}
              </Box>
            )}

            {step === TerminationSteps.SET_TERMINATION_PARAMS ? (
              <EmployerTerminationInfoBanner withGap />
            ) : null}

          </Box>

          {isTerminationDateError ? (
            <Box margin={{ top: 'spacing24' }}>
              <Inscription
                size="14px"
                color="danger"
                textAlign="end"
                lineHeight='20px'
              >
                {INVALID_DATE_RANGE_TEXT}
              </Inscription>
            </Box>
          ) : null}

          <Box
            direction="row"
            fill="horizontal"
            margin={{ top: 'spacing24' }}
            align="end"
            justify={editMode ? 'between' : 'end'}
            width="medium"
            gap="xs"
          >
            {editMode ? (
              <Box>
                <AppButton
                  testId="EmployerTerminationModalForm_cancel_termination"
                  buttonType="secondary"
                  width="control"
                  type="button"
                  onClick={handleCancelTermination}
                  color="red"
                >
                  Cancel Termination
                </AppButton>
              </Box>
            ) : null}
            <Box direction="row" gap="xs">
              <AppButton
                testId="EmployerTerminationModalForm_cancel"
                buttonType="secondary"
                width="control"
                type="button"
                onClick={handleCancel}
              >
                Cancel
              </AppButton>
              <AppButton
                testId="EmployerTerminationModalForm_submit"
                type="button"
                disabled={planListLoading || terminationLoading}
                onClick={handleSubmit}
                width="control"
              >
                {planListLoading || terminationLoading || terminationDataLoading ? (
                  <Preloader />
                ) : (
                  currentButtonTitle
                )}
              </AppButton>
            </Box>
          </Box>
        </Box>
      </Box>
      <WarnModal
        testId="employer-termination-server-error-modal"
        visible={isServerError}
        onSetVisible={setIsServerError}
        header="The server encountered an error processing the request"
        helptext={`It doesn't appear to have affected your data, but we cannot save the Employer termination with the latest records.
        Our technical staff have been automatically notified and will be looking into this with the utmost urgency.`}
        buttonText="Close"
        onSubmit={handleSubmit}
        submitButtonText="Try again"
      />
    </>
  );
};

export default EmployerTerminationModalForm;
